import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { of, tap } from 'rxjs';
import { AccountNotificationsActions } from '../actions/notifications.actions';
import { ApiServerService } from '../../shared/services/api-server.service';
import { AccountNotification } from '@smmof/entity';
import { SocketIoService } from '../../shared/services/socket.io.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../app.state';
import { GatewayNamespaces } from '@smmof/enum';
import { Socket } from 'socket.io-client';

@Injectable()
export class AccountNotificationsEffects {
  private socketInitialized = false;
  private socketNotification: Socket | null = null;

  constructor(
    private actions$: Actions,
    private apiService: ApiServerService,
    private socketIoService: SocketIoService,
    private store: Store<AppState>,
  ) {}

  // Эффект для загрузки уведомлений
  loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountNotificationsActions.loadNotifications),
      switchMap(() =>
        this.apiService.GET<AccountNotification[]>('account/notifications').pipe(
          map((notifications) => AccountNotificationsActions.loadNotificationsSuccess({ notifications })),
          catchError((error) => of(AccountNotificationsActions.loadNotificationsFailure({ error }))),
        ),
      ),
    ),
  );

  // Эффект для добавления уведомления
  addNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountNotificationsActions.addNotification),
      mergeMap(({ notification }) =>
        this.apiService.POST<AccountNotification>('account/notifications', notification).pipe(
          map((newNotification) =>
            AccountNotificationsActions.addNotificationSuccess({ notification: newNotification }),
          ),
          catchError((error) => of(AccountNotificationsActions.addNotificationFailure({ error }))),
        ),
      ),
    ),
  );

  // Эффект для обновления уведомления
  updateNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountNotificationsActions.updateNotification),
      mergeMap(({ notification }) =>
        this.apiService.PATCH<AccountNotification>(`account/notifications/${notification.id}`, notification).pipe(
          map((updatedNotification) =>
            AccountNotificationsActions.updateNotificationSuccess({ notification: updatedNotification }),
          ),
          catchError((error) => of(AccountNotificationsActions.updateNotificationFailure({ error }))),
        ),
      ),
    ),
  );

  // Эффект для удаления уведомления
  deleteNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountNotificationsActions.deleteNotification),
      mergeMap(({ id }) =>
        this.apiService.DELETE<boolean>(`account/notifications/${id}`).pipe(
          map(() => AccountNotificationsActions.deleteNotificationSuccess({ id })),
          catchError((error) => of(AccountNotificationsActions.deleteNotificationFailure({ error }))),
        ),
      ),
    ),
  );

  // Эффект для пометки всех уведомлений как прочитанных
  markAllAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountNotificationsActions.markAllAsRead),
      mergeMap(() =>
        this.apiService.POST<boolean>('account/notifications/mark-all-as-read', {}).pipe(
          map(() => AccountNotificationsActions.markAllAsReadSuccess()),
          catchError((error) => of(AccountNotificationsActions.markAllAsReadFailure({ error }))),
        ),
      ),
    ),
  );

  // Эффект для подключения к WebSocket и обработки входящих уведомлений
  connectWebSocket$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountNotificationsActions.connectWebSocket),
        mergeMap(async (action) => {
          await this.initializeSocket();
          await this.ensureSocketInitialized();
          return;
        }),
        tap(() => {
          if (this.socketNotification) {
            this.socketNotification.on('new-notification', (notification: AccountNotification) => {
              if (!notification) return;
              this.store.dispatch(
                AccountNotificationsActions.receiveWebSocketNotification({
                  notification,
                }),
              );
            });
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  // Эффект для обработки входящих уведомлений
  // receiveWebSocketNotification$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(AccountNotificationsActions.receiveWebSocketNotification),
  //     map(({ notification }) =>
  //       AccountNotificationsActions.addNotificationSuccess({ notification })
  //     )
  //   )
  // );

  private async initializeSocket() {
    if (!this.socketInitialized) {
      this.socketIoService.setupSocketConnection(GatewayNamespaces.NOTIFICATIONS);
      await this.ensureSocketInitialized();
      this.socketNotification = this.socketIoService.getSocket(GatewayNamespaces.NOTIFICATIONS);
      this.socketInitialized = true;
    }
  }

  private async ensureSocketInitialized() {
    return new Promise<void>((resolve) => {
      const checkSocket = () => {
        const socket = this.socketIoService.getSocket(GatewayNamespaces.NOTIFICATIONS);
        if (socket) {
          this.socketNotification = socket;
          resolve();
        } else {
          setTimeout(checkSocket, 100);
        }
      };
      checkSocket();
    });
  }
}
